/**
 * Klantaanvragen — wat klanten via het portaal insturen, hier in onze eigen app.
 *
 * De gegevens komen live uit het klantportaal (via /Api/aanvragen-portaal); we
 * houden hier geen tweede kopie bij. Links de werkbak, rechts de aanvraag met
 * beeldmateriaal en de twee dingen die je ermee doet: de status verzetten en de
 * klant antwoorden. Wat je stuurt komt met jouw naam in zijn tijdlijn.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, FormControlLabel, Link,
  Skeleton, Stack, Switch, TextField, Typography,
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import SendIcon from '@mui/icons-material/Send';
import toast from 'react-hot-toast';
import PageHeader from '../../ui-components/PageHeader';
import { authFetch } from '../../utils/authHelpers';
import { leesJson, meldFout } from '../../utils/apiFout';
import { BACKEND_URL } from '../../utils/apiConfig';

const ACCENT = { color: '#526075', bg: '#d5e3fd' };

const STATUS = {
  concept: { label: 'Nog niet verstuurd', kleur: 'default' },
  ontvangen: { label: 'Ontvangen', kleur: 'info' },
  prijs_bevestigd: { label: 'Prijs bevestigd', kleur: 'info' },
  in_productie: { label: 'In productie', kleur: 'warning' },
  eerste_versie: { label: 'Eerste versie klaar', kleur: 'success' },
  afgerond: { label: 'Afgerond', kleur: 'success' },
  geannuleerd: { label: 'Geannuleerd', kleur: 'error' },
};
const VOLGORDE = ['ontvangen', 'prijs_bevestigd', 'in_productie', 'eerste_versie', 'afgerond', 'geannuleerd'];
const LOOPT = ['ontvangen', 'prijs_bevestigd', 'in_productie', 'eerste_versie'];

const VELDEN = [
  ['Kanalen', (d) => (d.wat?.kanalen || d.draaidag?.kanalen || []).join(', ')],
  ['Verhaal', (d) => d.wensen?.verhaal || d.draaidag?.gebeurt],
  ['Titels in beeld', (d) => jaNee(d.wensen?.titelInBeeld)],
  ['Ondertitels', (d) => jaNee(d.wensen?.ondertitels)],
  ['Link naar beelden', (d) => d.beelden?.link || d.films?.link],
  ['Aantal films', (d) => d.films?.aantal],
  ['Taal', (d) => d.ondertitels?.taal],
  ['Vorm', (d) => d.ondertitels?.vorm],
  ['Namen en vaktermen', (d) => d.ondertitels?.namen],
  ['Datum', (d) => d.waar?.datum],
  ['Locatie', (d) => d.waar?.locatie],
  ['Begintijd', (d) => d.waar?.begintijd],
  ['Duur', (d) => d.waar?.duur],
  ['Montage gewenst', (d) => jaNee(d.draaidag?.montage)],
];

function jaNee(v) {
  if (v === undefined || v === null) return '';
  return v ? 'Ja' : 'Nee';
}

function tijd(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function omvang(bytes) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  if (bytes < 1024 * 1024 * 1024) return `${Math.round(bytes / 1024 / 1024)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export default function AanvragenPortaal() {
  const [rijen, setRijen] = useState(null);
  const [fout, setFout] = useState(null);
  const [gekozen, setGekozen] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailBezig, setDetailBezig] = useState(false);
  const [alles, setAlles] = useState(false);
  const [tekst, setTekst] = useState('');
  const [intern, setIntern] = useState(false);
  const [bezig, setBezig] = useState(false);

  const haalLijst = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/aanvragen-portaal`);
      const data = await leesJson(r, 'Klantaanvragen laden');
      setRijen(data.aanvragen || []);
      setFout(null);
    } catch (e) {
      setFout(e.message || 'Laden mislukt.');
      setRijen([]);
    }
  }, []);

  useEffect(() => { haalLijst(); }, [haalLijst]);

  // Welke aanvraag staat er nú open? Klik je snel door de lijst, dan kan een traag
  // antwoord van de vorige aanvraag later binnenkomen dan dat van de nieuwe. Zonder
  // deze controle zag je dan gegevens van A terwijl B geselecteerd is — en een
  // statuswijziging of antwoord landde bij de verkeerde klant.
  const gekozenRef = useRef(null);
  gekozenRef.current = gekozen;

  const haalDetail = useCallback(async (id) => {
    if (!id) return;
    setDetailBezig(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/aanvragen-portaal/${encodeURIComponent(id)}`);
      const data = await leesJson(r, 'Aanvraag laden');
      if (gekozenRef.current !== id) return;   // ondertussen iets anders aangeklikt
      setDetail(data.aanvraag || null);
    } catch (e) {
      if (gekozenRef.current !== id) return;
      meldFout(e, 'Aanvraag laden');
      setDetail(null);
    } finally {
      if (gekozenRef.current === id) setDetailBezig(false);
    }
  }, []);

  // Bij een wissel eerst het oude detail weg: anders staat de vorige aanvraag nog
  // in beeld terwijl de nieuwe laadt, met knoppen die op de verkeerde rij werken.
  useEffect(() => {
    if (!gekozen) return;
    setDetail(null);
    haalDetail(gekozen);
  }, [gekozen, haalDetail]);

  const zichtbaar = useMemo(() => {
    const lijst = rijen || [];
    return alles ? lijst : lijst.filter((r) => LOOPT.includes(r.status));
  }, [rijen, alles]);

  const wachten = (rijen || []).filter((r) => r.onbeantwoord || r.status === 'ontvangen').length;

  const stuur = async (body, melding) => {
    setBezig(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/aanvragen-portaal/${encodeURIComponent(gekozen)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      await leesJson(r, 'Aanvraag bijwerken');
      toast.success(melding);
      setTekst('');
      await Promise.all([haalDetail(gekozen), haalLijst()]);
    } catch (e) {
      meldFout(e, 'Aanvraag bijwerken');
    } finally {
      setBezig(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>
      <PageHeader
        icon="inventory_2"
        accent={ACCENT}
        title="Klantaanvragen"
        subtitle={rijen ? `Uit het klantportaal · ${wachten} wachten op ons` : 'Uit het klantportaal'}
        rightActions={
          <FormControlLabel
            control={<Switch checked={alles} onChange={(e) => setAlles(e.target.checked)} size="small" />}
            label="Toon ook afgeronde"
          />
        }
      />

      {fout ? <Alert severity="error" sx={{ mb: 2 }}>{fout}</Alert> : null}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '380px minmax(0, 1fr)' }, gap: 3, alignItems: 'start' }}>
        {/* Werkbak */}
        <Stack spacing={1}>
          {!rijen ? (
            [0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={78} />)
          ) : zichtbaar.length === 0 ? (
            <Alert severity="info">Er staat nu niets open.</Alert>
          ) : (
            zichtbaar.map((r) => {
              const actief = r.id === gekozen;
              return (
                <Box
                  key={r.id}
                  component="button"
                  type="button"
                  onClick={() => setGekozen(r.id)}
                  sx={{
                    textAlign: 'left', font: 'inherit', color: 'inherit', cursor: 'pointer', width: '100%',
                    p: 2, borderRadius: 2, bgcolor: actief ? ACCENT.bg : 'background.paper',
                    border: '1px solid', borderColor: actief ? ACCENT.color : 'divider',
                    '&:hover': { borderColor: ACCENT.color },
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    {r.onbeantwoord ? <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main', flexShrink: 0 }} /> : null}
                    <Typography sx={{ fontWeight: 700, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.titel}
                    </Typography>
                    <Chip size="small" label={STATUS[r.status]?.label || r.status} color={STATUS[r.status]?.kleur || 'default'} variant="outlined" />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {r.account || 'zonder account'}
                    {r.nummer ? ` · #${r.nummer}` : ''}
                    {r.aantal_bestanden ? ` · ${r.aantal_bestanden} bestand${r.aantal_bestanden === 1 ? '' : 'en'}` : ''}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {r.richtprijs_tekst ? `${r.richtprijs_tekst} · ` : ''}{tijd(r.laatste_activiteit)}
                  </Typography>
                </Box>
              );
            })
          )}
        </Stack>

        {/* Aanvraag */}
        <Box sx={{ minWidth: 0 }}>
          {!gekozen ? (
            <Alert severity="info">Kies links een aanvraag.</Alert>
          ) : detailBezig && !detail ? (
            <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress /></Box>
          ) : !detail ? (
            <Alert severity="warning">Deze aanvraag kon niet geladen worden.</Alert>
          ) : (
            <Stack spacing={3}>
              <Box sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>{detail.titel}</Typography>
                  <Chip size="small" label={STATUS[detail.status]?.label || detail.status} color={STATUS[detail.status]?.kleur || 'default'} />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {detail.nummer ? `#${detail.nummer} · ` : ''}{detail.account || 'zonder account'} · {detail.aanvrager?.naam || detail.aanvrager?.email}
                  {detail.ingediend_op ? ` · verstuurd ${tijd(detail.ingediend_op)}` : ''}
                  {detail.gripp_offer_nummer ? ` · Gripp-offerte ${detail.gripp_offer_nummer}` : ''}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ mb: 1 }}>Waar staat het</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {VOLGORDE.map((s) => (
                    <Button
                      key={s}
                      size="small"
                      variant={s === detail.status ? 'contained' : 'outlined'}
                      disabled={bezig || s === detail.status}
                      onClick={() => stuur({ status: s }, `Status staat nu op "${STATUS[s].label}". De klant ziet dat ook.`)}
                      sx={{ textTransform: 'none' }}
                    >
                      {STATUS[s].label}
                    </Button>
                  ))}
                </Stack>
              </Box>

              <Box sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>De aanvraag</Typography>
                <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '200px minmax(0, 1fr)' }, rowGap: 1, columnGap: 2 }}>
                  {detail.pakket_naam ? (<><Box component="dt" sx={{ color: 'text.secondary' }}>Pakket</Box><Box component="dd" sx={{ m: 0 }}>{detail.pakket_naam}</Box></>) : null}
                  {detail.richtprijs_tekst ? (<><Box component="dt" sx={{ color: 'text.secondary' }}>Richtprijs</Box><Box component="dd" sx={{ m: 0 }}>{detail.richtprijs_tekst} excl. btw</Box></>) : null}
                  {detail.deadline ? (<><Box component="dt" sx={{ color: 'text.secondary' }}>Gewenste oplevering</Box><Box component="dd" sx={{ m: 0 }}>{detail.deadline}</Box></>) : null}
                  {VELDEN.map(([label, lees]) => {
                    const waarde = detail.data ? lees(detail.data) : '';
                    if (!waarde) return null;
                    return (
                      <Box key={label} sx={{ display: 'contents' }}>
                        <Box component="dt" sx={{ color: 'text.secondary' }}>{label}</Box>
                        <Box component="dd" sx={{ m: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{String(waarde)}</Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              <Box sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Beeldmateriaal</Typography>
                {(detail.bestanden || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">Geen bestanden geüpload. Kijk of er een link bij de aanvraag staat.</Typography>
                ) : (
                  <Stack spacing={0.75}>
                    {detail.bestanden.map((b) => (
                      <Stack key={b.id} direction="row" spacing={1} alignItems="center">
                        <InsertDriveFileIcon fontSize="small" color="disabled" />
                        {b.download_url ? (
                          <Link href={b.download_url} target="_blank" rel="noopener" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{b.naam}</Link>
                        ) : (
                          <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{b.naam}</Typography>
                        )}
                        <Typography variant="caption" color="text.disabled" sx={{ whiteSpace: 'nowrap' }}>
                          {b.status === 'klaar' ? omvang(b.bytes) : 'upload mislukt'}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5 }}>
                  Downloadlinks zijn een uur geldig; klik de aanvraag opnieuw aan voor nieuwe.
                </Typography>
              </Box>

              <Box sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Tijdlijn</Typography>
                <Stack spacing={1.5} component="ol" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                  {(detail.events || []).map((e) => (
                    <Box component="li" key={e.id}>
                      <Typography variant="body2" color="text.secondary">
                        <b>{e.door || 'Systeem'}</b> · {tijd(e.at)}
                        {e.intern ? <Chip size="small" label="intern" sx={{ ml: 1 }} /> : null}
                        {e.type === 'status' && e.status ? <Chip size="small" sx={{ ml: 1 }} label={STATUS[e.status]?.label || e.status} /> : null}
                      </Typography>
                      {e.tekst ? <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{e.tekst}</Typography> : null}
                    </Box>
                  ))}
                </Stack>

                <Stack spacing={1} sx={{ mt: 2 }}>
                  <TextField
                    multiline
                    minRows={2}
                    value={tekst}
                    onChange={(e) => setTekst(e.target.value)}
                    placeholder="Antwoord aan de klant…"
                    fullWidth
                  />
                  <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                    <FormControlLabel
                      control={<Switch checked={intern} onChange={(e) => setIntern(e.target.checked)} size="small" />}
                      label="Interne notitie (klant ziet dit niet)"
                    />
                    <Button
                      variant="contained"
                      startIcon={<SendIcon />}
                      disabled={bezig || tekst.trim().length < 2}
                      onClick={() => stuur({ tekst, intern }, intern ? 'Notitie opgeslagen.' : 'Verstuurd naar de klant.')}
                      sx={{ textTransform: 'none' }}
                    >
                      {intern ? 'Notitie opslaan' : 'Stuur naar de klant'}
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Stack>
          )}
        </Box>
      </Box>
    </Box>
  );
}
